import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FiLock } from 'react-icons/fi';
import { FiChevronsLeft, FiChevronLeft, FiChevronRight, FiChevronsRight } from 'react-icons/fi';

import ContractorAuthenticationModal from '../../../components/family-plaza/ContractorAuthenticationModal';
import PortraitPhotoRegistrationModal from '../../../components/family-plaza/PortraitPhotoRegistrationModal';
import { checkModalAuthenticationStatus } from '../../../utils/authUtils';
import { getCurrentUserProfilePhoto, getCurrentCustomerName, setupProfilePhotoListener, getDeceasedPersonDate, getDeceasedPersonProfileText } from '../../../utils/profilePhotoUtils';
import CyberMemorialService from '../../../services/FamilyPlaza/CyberMemorialService';
import { getBoardList, getCherishLettersByGoinId } from '../../../api';
import FamilyPlazaSubHeading from '../../../components/family-plaza/FamilyPlazaSubHeading';
import FamilyPlazaTopSection from '../../../components/family-plaza/FamilyPlazaTopSection';
import DeceasedPersonProfile from '../../../components/family-plaza/DeceasedPersonProfile';



const MemorialPostBoardListPC = () => {
  const navigate = useNavigate();
  const [memorialPosts, setMemorialPosts] = useState([]);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState('/donate/layer-1910.png');
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage] = useState(10);
  const handleNavigation = (route) => { navigate(route); };

  // Calculate pagination
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = memorialPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(memorialPosts.length / postsPerPage);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Generate page numbers array
  const getPageNumbers = () => {
    const pageNumbers = [];
    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

 

  // Load memorial posts from API first, then localStorage as fallback
  useEffect(() => {
    const loadMemorialPosts = async () => {
      const selectedPersonData = localStorage.getItem('selectedPersonData');
      let customerName = 'default';
      let goinId = null;
      
      if (selectedPersonData) {
        try {
          const personData = JSON.parse(selectedPersonData);
          customerName = personData.customerName || 'default';
          goinId = personData.goinId || null;
        } catch (error) {
          customerName = 'default';
        }
      }
      
      try {
        // Use cherish letters by goinId API
        if (goinId) {
          const cherishRes = await getCherishLettersByGoinId(goinId);
          if (cherishRes && Array.isArray(cherishRes.data)) {
            const processedPosts = cherishRes.data.map(item => ({
              id: item.letterId || item.id,
              title: item.letterTitle || '제목 없음',
              content: item.letterContents || '',
              author: item.writerName || (item.registId ? String(item.registId) : (item.registIp || 'Anonymous')),
              writerName: item.writerName || '',
              date: item.registTime,
              isPrivate: (item.secureFlag || 'Y') === 'Y',
              password: '',
            customerName: customerName
          }));
          if (processedPosts.length > 0) {
              const sortedPosts = processedPosts.sort((a, b) => new Date(b.date) - new Date(a.date));
            setMemorialPosts(sortedPosts);
            const postsKey = `memorialPosts_${customerName}`;
            localStorage.setItem(postsKey, JSON.stringify(sortedPosts));
            return;
            }
          }
        }
      } catch (error) {
        console.log('Cherish letters API failed:', error);
      }

      // Fallback to localStorage if API fails or returns no data
      const memorialPostsKey = `memorialPosts_${customerName}`;
      const savedPosts = localStorage.getItem(memorialPostsKey);
      
      if (savedPosts) {
        try {
          const parsedPosts = JSON.parse(savedPosts);
          // Sort by date - most recent first
          const sortedPosts = parsedPosts.sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            return dateB - dateA; // Descending order (newest first)
          });
          setMemorialPosts(sortedPosts);
        } catch (error) {
          setMemorialPosts([]);
        }
      } else {
        setMemorialPosts([]);
      }
    };

    loadMemorialPosts();
  }, []);

  // Listen for memorial post updates
  useEffect(() => {
    const handleMemorialPostUpdate = (event) => {
      const selectedPersonData = localStorage.getItem('selectedPersonData');
      let customerName = 'default';
      
      if (selectedPersonData) {
        try {
          const personData = JSON.parse(selectedPersonData);
          customerName = personData.customerName || 'default';
        } catch (error) {
          customerName = 'default';
        }
      }
      
      if (event.detail.customerName === customerName) {
        const postsKey = `memorialPosts_${customerName}`;
        const savedPosts = localStorage.getItem(postsKey);
        if (savedPosts) {
          try {
            const parsedPosts = JSON.parse(savedPosts);
            // Sort by date - most recent first
            const sortedPosts = parsedPosts.sort((a, b) => {
              const dateA = new Date(a.date);
              const dateB = new Date(b.date);
              return dateB - dateA; // Descending order (newest first)
            });
            setMemorialPosts(sortedPosts);
          } catch (error) {
            setMemorialPosts([]);
          }
        }
      }
    };

    window.addEventListener('memorialPostUpdated', handleMemorialPostUpdate);
    return () => {
      window.removeEventListener('memorialPostUpdated', handleMemorialPostUpdate);
    };
  }, []);

  const handlePostClick = (postId) => {
    // Save the selected post data to localStorage for details page
    const selectedPost = memorialPosts.find(post => post.id === postId);
    if (selectedPost) {
      localStorage.setItem('selectedMemorialPost', JSON.stringify(selectedPost));
      navigate('/memorial-post-details');
    }
  };

  // Photo icon click handler
  const handlePhotoIconClick = () => {
    const isAuthenticated = checkModalAuthenticationStatus();
    
    if (isAuthenticated) {
      // If already authenticated, open photo modal directly
      setShowPhotoModal(true);
    } else {
      // If not authenticated, open auth modal first
      setShowAuthModal(true);
    }
  };

  const handleWritePost = () => {
    // Check modal authentication status first
    if (checkModalAuthenticationStatus()) {
      // Clear any existing selected post data to ensure fresh writing mode
      localStorage.removeItem('selectedMemorialPost');
      navigate('/memorial-post-writing');
    } else {
      // First show authentication modal
      setShowAuthModal(true);
    }
  };

  const handleAuthConfirm = () => {
    setShowAuthModal(false);
    // Navigate to writing page after successful authentication
    navigate('/memorial-post-writing');
  };

  const handleAuthClose = () => {
    setShowAuthModal(false);
  };

  // Photo registration modal handlers
  const handlePhotoConfirm = () => {
    setShowPhotoModal(false);
  };

  const handlePhotoUpload = (photoUrl) => {
    console.log('Photo uploaded, updating profile photo:', photoUrl.substring(0, 50) + '...');
    // Update profile photo immediately when photo is uploaded
    setProfilePhotoUrl(photoUrl);
  };

  const handlePhotoClose = () => {
    setShowPhotoModal(false);
  };



  return (
    <div className="memorial-post-board-list-pc page-memorial-post-board-list-pc announcement-list-page cyber-info-page-pc">
      
    <FamilyPlazaTopSection />

   <main className="main-content">

    <FamilyPlazaSubHeading heading="사이버추모관" />
      {/* Main Content */}
      <div className="donate-registration-main-content">
        <div className="donate-registration-page-content">
          <div className="donate-registration-container">
            
       
            <DeceasedPersonProfile 
              handlePhotoIconClick={handlePhotoIconClick}
            />

            {/* Action Tabs Navigation */}
            <div className="donate-registration-action-tabs">
              <div className="donate-registration-action-tabs-container">
                <ul>
                  <li><Link to="/donate">헌화하기</Link></li>
                  <li><Link to="/memorial-post-board-list" className="active">추모글</Link></li>
                  <li><Link to="/memorial-album-board-list">추모앨범</Link></li>
                </ul>
              </div>
            </div>

            {/* Content Area - Memorial Post List */}
            <div className="memorial-post-board-list-content-area">
              
              {/* Memorial Post Entries */}
              <div className="memorial-post-board-list-entries">
                {/* Show user's memorial posts (latest first) */}
                {currentPosts.length > 0 ? (
                  currentPosts.map((post) => (
                    <div key={post.id} className="memorial-post-board-list-entry" onClick={() => handlePostClick(post.id)} style={{ cursor: 'pointer' }}>
                      <div className="memorial-post-board-list-entry-left">
                        <span className="memorial-post-board-list-entry-title">{post.title}</span>
                        <FiLock className="memorial-post-board-list-lock-icon" />
                      </div>
                      <div className="memorial-post-board-list-entry-middle">
                        <span className="memorial-post-board-list-sender">From. {post.writerName || post.customerName}</span>
                      </div>
                      <div className="memorial-post-board-list-entry-right">
                        <span className="memorial-post-board-list-entry-date">
                          {new Date(post.date).toLocaleDateString('ko-KR', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit'
                          }).replace(/\. /g, '.').replace(/\.$/, '')}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="memorial-post-board-list-no-posts">
                    <p>아직 추모글이 없습니다. 첫 번째 추모글을 작성해보세요.</p>
                  </div>
                )}
              </div>
              
              {/* Pagination */}
              <div className="memorial-post-board-list-pagination">
                <button 
                  className="memorial-post-board-list-pagination-btn"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                >
                  <FiChevronsLeft />
                </button>
                <button 
                  className="memorial-post-board-list-pagination-btn"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <FiChevronLeft />
                </button>
                {getPageNumbers().map((pageNumber) => (
                  <button
                    key={pageNumber}
                    className={`memorial-post-board-list-pagination-btn ${currentPage === pageNumber ? 'active' : ''}`}
                    onClick={() => handlePageChange(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                ))}
                <button 
                  className="memorial-post-board-list-pagination-btn"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <FiChevronRight />
                </button>
                <button 
                  className="memorial-post-board-list-pagination-btn"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  <FiChevronsRight />
                </button>
              </div>
              
              {/* Bottom Action Bar */}
              <div className="memorial-post-board-list-bottom-action">
                <div className="memorial-post-board-list-checkbox-container">
                  <img src="/images/icon4.png" alt="Check" className="letter-to-heaven-board-list-check-icon" />
                  <label htmlFor="memorial-checkbox" className="memorial-post-board-list-checkbox-label">
                    그리고 보고싶은 사람에게 추모글을 남겨주세요.
                  </label>
                </div>
                <button onClick={handleWritePost} className="memorial-post-board-list-write-btn">글쓰기</button>
              </div>
              
            </div>

          </div>
        </div>
      </div>

      

      {/* Authentication Modal */}
      <ContractorAuthenticationModal
        isOpen={showAuthModal}
        onConfirm={handleAuthConfirm}
        onClose={handleAuthClose}
      />

      {/* Photo Registration Modal */}
      <PortraitPhotoRegistrationModal
        isOpen={showPhotoModal}
        onConfirm={handlePhotoConfirm}
        onClose={handlePhotoClose}
        onPhotoUpload={handlePhotoUpload}
      />
      </main>
    </div>
  );
};

export default MemorialPostBoardListPC; 
